// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  message: string;
  type: ToastType;
  duration?: number; // Duración en ms, 0 para permanente
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, duration = 3000, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const reduceMotion = useReducedMotion();

  const handleClose = useCallback(() => {
    setIsVisible(false);
    // Dar tiempo a la animación de salida antes de llamar a onClose
    setTimeout(onClose, 300); 
  }, [onClose]);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(handleClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  const iconMap = {
    success: <CheckCircle className="w-5 h-5 text-green-600" />,
    error: <XCircle className="w-5 h-5 text-red-600" />,
    info: <Info className="w-5 h-5 text-blue-600" />,
  } as const;

  // Unified color tokens
  const tone = {
    success: { bg: 'bg-green-50', border: 'border-green-200', ring: 'ring-green-300' },
    error: { bg: 'bg-red-50', border: 'border-red-200', ring: 'ring-red-300' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', ring: 'ring-blue-300' },
  } as const;

  return isVisible ? (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 6 }}
      transition={{ duration: reduceMotion ? 0 : 0.25, ease: 'easeOut' }}
      role="status"
      aria-live="polite"
      className={`pointer-events-auto p-4 rounded-xl shadow-lg flex items-center gap-3 border ${tone[type].bg} ${tone[type].border} ring-1 ${tone[type].ring} z-50 max-w-sm`}
    >
      {iconMap[type]}
      <p className="text-gray-800 font-medium flex-1">{message}</p>
      <button onClick={handleClose} className="text-gray-500 hover:text-gray-700">
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  ) : null;
};

export default Toast;